import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import NodeAPI from '../api';
import QuizQuestionCard from './QuizQuestionCard';

function nextUnanswered(session) {
  return session.attempts.findIndex(attempt => !attempt.answeredAt);
}
export default function DailyQuiz() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [position, setPosition] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setLoading(true); setError(''); setShowFeedback(false);
    NodeAPI.get('/quiz/notifications/sessions/' + sessionId)
      .then(({ data }) => {
        setSession(data);
        const next = nextUnanswered(data);
        setPosition(next < 0 ? data.attempts.length : next);
        NodeAPI.post('/quiz/notifications/sessions/' + sessionId + '/open').catch(() => {});
      })
      .catch(err => setError(err.response?.status === 404 ? 'This daily quiz is unavailable.' :
        'Could not load the daily quiz. Check your connection.'))
      .finally(() => setLoading(false));
  }, [sessionId]);
  const submit = async selected => {
    if (submitting || !session) return;
    const attempt = session.attempts[position];
    setSubmitting(true); setError('');
    try {
      const { data } = await NodeAPI.post('/quiz/notifications/sessions/' + sessionId +
        '/attempts/' + attempt.id + '/answer', { answer: selected });
      setSession(data);
      setShowFeedback(true);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.attempts) {
        const current = err.response.data;
        setSession(current);
        setPosition(nextUnanswered(current));
        setShowFeedback(false);
        setError('This quiz was updated on another device. Continue from the next question.');
      } else {
        setError(err.response?.status === 410 ? 'This daily quiz has expired.' :
          'Could not submit your answer. Check your connection and try again.');
      }
    } finally { setSubmitting(false); }
  };
  if (loading) return <Box sx={{ textAlign: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (error && !session) return <Box sx={{ maxWidth: 600, mx: 'auto', mt: 5 }}><Alert severity="error">{error}</Alert></Box>;
  const attempts = session.attempts;
  const attempt = attempts[position];
  const expired = new Date(session.expiresAt) <= new Date() && !session.completedAt;
  const complete = session.completedAt && !showFeedback;
  return <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
    <Card sx={{ boxShadow: 3, p: 2 }}><CardContent>
      <Typography variant="h5" fontWeight="bold">Daily Quiz</Typography>
      {complete ? <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Daily quiz complete</Typography>
        <Typography>{session.score} / {session.actualQuestionCount} correct</Typography>
      </Box> : expired ? <Alert severity="info" sx={{ mt: 2 }}>This daily quiz has expired.</Alert> : attempt && <>
        <Typography sx={{ mt: 2 }}>Question {attempt.position} of {session.actualQuestionCount}</Typography>
        <QuizQuestionCard key={attempt.id} question={attempt.question} options={attempt.options}
          selectedAnswer={showFeedback ? attempt.selectedAnswer : undefined}
          correctAnswer={showFeedback ? attempt.correctAnswer : undefined}
          disabled={submitting || showFeedback} onAnswer={submit} />
        {showFeedback && <>
          <Alert severity={attempt.isCorrect ? 'success' : 'info'} sx={{ mt: 2 }}>
            {attempt.isCorrect ? 'Correct!' : `Incorrect. Correct answer: ${attempt.correctAnswer}`}
          </Alert>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => {
            const next = nextUnanswered(session);
            setPosition(next < 0 ? attempts.length : next);
            setShowFeedback(false);
          }}>{session.completedAt ? 'View Results' : 'Next Question'}</Button>
        </>}
      </>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </CardContent></Card>
    <Button component={Link} to="/categories" sx={{ mt: 2 }}>Back to Categories</Button>
  </Box>;
}
